import ClientUtils from "../../../../utils/ClientUtils";

import { IconStar } from "@tabler/icons-react";
import { useMemo } from "react";
import { SimplifiedBookingSuggestions } from "../../../../_model/repos/BookingSuggestionsApiClient";
import MyLink from "../../../../components/routing/MyLink";
import AppRoutes from "../../../../routes";

type Suggestion = SimplifiedBookingSuggestions["orderedSuggestions"][number];

interface Props {
  className?: string;
  suggestions: SimplifiedBookingSuggestions;
}

const SimplifiedBookingSuggestionsResultsView = (props: Props) => {
  return (
    <section
      className={ClientUtils.classNames(
        "grid h-full w-full grid-rows-[auto,minmax(0,1fr)]",
        props.className
      )}
    >
      <header className="grid grid-cols-[75px,minmax(0,1fr),minmax(0,1fr)] border-b border-gray-100 ">
        <div className="col-span-2 flex items-center gap-x-4 py-2">
          <div className="rounded-full bg-stone-600 px-4 py-1 text-xs text-light-background">
            {props.suggestions.address.name}
          </div>
          <div className="rounded-full bg-stone-600 px-4 py-1 text-xs text-light-background">
            {props.suggestions.qualification}
          </div>
        </div>

        <h3 className="mt-auto text-xs text-stone-500">Intern notering</h3>
      </header>

      <section className="flex flex-col gap-6 overflow-y-auto">
        <ul className="flex flex-col gap-2">
          {props.suggestions.orderedSuggestions.map((sug) => {
            return (
              <SuggestionListItem
                key={`${sug.handyman.id}-simplified-suggestion`}
                suggestion={sug}
              />
            );
          })}
        </ul>
      </section>
    </section>
  );
};

const SuggestionListItem = (props: { suggestion: Suggestion }) => {
  const sug = props.suggestion;
  const handyman = sug.handyman;

  const adressText = useMemo(() => {
    const { street, city, postalCode } = handyman;
    const hasAddress = !!street && !!city;
    if (!hasAddress) {
      return "Adress saknas";
    }

    if (!!postalCode) {
      return `${street}, ${postalCode} ${city}`;
    }

    return `${street}, ${city}`;
  }, [handyman]);

  return (
    <li className="grid w-full grid-cols-[75px,minmax(0,1fr),minmax(0,1fr)] grid-rows-1 overflow-hidden rounded-lg border border-stone-300">
      <ul className="flex flex-col gap-2 bg-stone-500 p-2 text-sm text-light-background [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-stone-400 [&>*:not(:last-child)]:pb-2">
        <li>{sug.travelInfo.duration.text}</li>
        <li>{sug.travelInfo.distance.text}</li>
      </ul>
      <section className="flex flex-col justify-center gap-2 bg-stone-50 px-4 py-2">
        <div className="flex items-center gap-2">
          <MyLink
            to={
              AppRoutes.salesTeam.singlePartner(handyman.id) + "?sida=kalender"
            }
            blank
            className="items-center font-semibold hover:text-brand-blue-400"
          >
            <span className="flex items-center gap-2">
              <span className="flex text-base">{`${handyman.firstName} ${handyman.lastName} `}</span>

              <span className="my-auto flex pt-[1px] text-xs italic opacity-80">
                {handyman.companyName}
              </span>
            </span>
          </MyLink>
          {sug.isPrioritized && <IconStar className="mt-[1px]" size={12} />}
        </div>

        <div className="flex items-center gap-2 [&>*:not(:last-child)]:border-r [&>*:not(:last-child)]:pr-2">
          {!!handyman.phone && (
            <a
              className="items-center gap-2 text-sm text-brand-blue-600 hover:underline"
              href={`tel:${handyman.phone}`}
            >
              <span>{handyman.phone}</span>
            </a>
          )}
          <p className="text-sm">{adressText}</p>
        </div>
      </section>

      <section className="bg-stone-100/80 p-2 pl-4">
        {handyman.internalDescription && (
          <pre className="max-w-prose whitespace-pre-wrap text-sm">
            {handyman.internalDescription}
          </pre>
        )}
      </section>
    </li>
  );
};

export default SimplifiedBookingSuggestionsResultsView;
